/**
 * Quiz — "Find My Match"
 * -----------------------------------------------------------------------------
 * STANDALONE script. NOT a webpack build output — hand-authored and safe to edit
 * directly. Deliberately kept out of dev/src/js/sections/ (which webpack
 * auto-globs) so that adding this quiz never forces a rebuild of vendor.js /
 * common.js / theme.js. See CLAUDE.md "Webpack JS bundle drift".
 *
 * Paired with: sections/quiz-match.liquid, assets/quiz-match.css
 *
 * RELATION TO THE "FIND YOUR RITUAL" QUIZ
 * This is a FORK of assets/quiz-ritual.js, not a variant. That quiz is live on
 * paid traffic and is not touched. The step engine, persistence, history
 * handling, Klaviyo reporting, loading interstitial and review carousel are
 * carried over verbatim because they are proven; what changes is the question
 * set, the answer -> bundle mapping, and three engine fixes the intro screen
 * needs (marked FORK CHANGE below).
 *
 * Responsibilities:
 *   - one visible step at a time, driven off data attributes in the markup
 *   - single-select  -> mark, persist, auto-advance
 *   - multi-select   -> toggle up to data-max, "none of these" is mutually
 *                       exclusive, commit via next()
 *   - progress bar stays in sync with the step number; the intro screen is
 *     step 0, so its bar is empty
 *   - answers persist to sessionStorage so a refresh resumes in place
 *   - device/browser Back steps back through the quiz instead of leaving it
 *   - the review carousel (separate ReviewsSlider, reader-driven only, shares
 *     nothing with the step engine beyond living in the same file)
 *   - the email screen: validation, consent gate, and the Klaviyo subscribe +
 *     event pair that records the lead and every answer
 *   - the loading interstitial: cycles its three photos, then hands off to the
 *     results (skipped entirely when there is no results destination)
 *
 * BUILT SO FAR: step 0 (intro). Question steps are added as their Figma frames
 * land; the engine already supports them, so each one is markup + CSS only.
 *
 * Public surface:
 *   window.FroyaMatchQuiz.getAnswers()   -> { focus: 'skin', ... }
 *   window.FroyaMatchQuiz.getIdentity()  -> { first_name, email, ... }
 *   window.FroyaMatchQuiz.isComplete()   -> bool
 *   document  'quiz-match:answer'        -> { questionId, value, answers }
 *   document  'quiz-match:step'          -> { index, stepNumber, total }
 *   document  'quiz-match:complete'      -> { answers }
 *   document  'quiz-match:email'         -> { identity, answers }
 */
(function () {
  'use strict';

  var AUTO_ADVANCE_DELAY = 300; // matches the existing quiz funnel
  var STORAGE_VERSION = 1;

  /* ---------------------------------------------------------------- klaviyo -- */
  /**
   * Client-side Subscribe API — no klaviyo.js dependency, no backend. The Public
   * API Key (company_id) and the List ID come from the section settings and are
   * read off the section root's data-* attributes; blank either one and the
   * quiz simply stops reporting.
   *
   * Same shape as the /pages/quiz funnel (dev/src/js/sections/section-quiz-funnel.js)
   * on purpose — it is proven in production, and keeping the two identical means
   * one mechanism to reason about.
   *
   * What is deliberately NOT shared is the NAMING. Every metric, property and
   * source string is prefixed with the quiz identifier from the section settings
   * ("Find My Match"), so this quiz's answers can never be confused with, or
   * silently overwrite, the older funnel's "Quiz *" properties on the same
   * profile. A visitor can take both quizzes and both records survive.
   */
  var KLAVIYO_REVISION = '2024-10-15';
  var DEFAULT_QUIZ_NAME = 'Find My Match';

  // Every answer is sent to Klaviyo TWICE: the raw key (stable — the on-page
  // copy can be reworded without breaking an existing segment) and a human
  // label (for email templates via {{ person|lookup:'… Label' }}).
  // KEEP IN SYNC with the data-value attributes in sections/quiz-match.liquid.
  //
  // Only Q1 exists so far. Add each question's block as its design lands — a
  // question missing from these maps still works on the page, it simply reports
  // its raw value to Klaviyo with no label.
  var ANSWER_LABELS = {
    focus: {
      skin: 'My skin',
      hair: 'My hair and scalp',
      both: 'Both'
    },
    // Q2's values are SHARED across its three variants on purpose — a concern
    // means the same thing whichever path reached it, so one slug, one label.
    concerns: {
      fine_lines: 'Fine lines and loss of firmness',
      dark_circles: 'Dark circles and under-eye puffiness',
      dullness: 'Dullness and uneven tone',
      dryness_redness: 'Dryness, redness or irritation',
      breakouts: 'Breakouts and congestion',
      thinning: 'Thinning and less volume',
      hair_fall: 'Hair fall and shedding',
      itchy_scalp: 'Itchy or irritated scalp',
      flakes: 'Flakes and dryness',
      slow_growth: 'Slow growth'
    },
    duration: {
      few_months: "It's fairly new, a few months",
      about_a_year: 'About a year',
      few_years: 'A few years now',
      always: 'As long as I can remember'
    },
    sensitivity: {
      very_sensitive: 'Very sensitive. I have to be careful',
      somewhat_sensitive: 'Somewhat sensitive',
      not_sensitive: 'Not sensitive'
    },
    urgency: {
      today: 'I want to start today',
      soon: "I'm looking to start soon",
      thinking: "I'm thinking about it"
    },
    lifestyle: {
      active_outdoors: 'Active and outdoors',
      working_out_a_lot: 'Working and out a lot',
      caring_for_others: 'Looking after other people',
      home_focused: 'Home more, focused on myself'
    }
  };

  // Question ID -> the property-name stem. The quiz identifier is prepended at
  // send time, so these read as "Find My Match Focus" in Klaviyo — distinct
  // from the ritual quiz's "Find Your Ritual *" and the old funnel's "Quiz *",
  // so all three can sit on one profile without overwriting each other.
  var PROPERTY_STEMS = {
    focus: 'Focus',
    concerns: 'Concerns',
    duration: 'Duration',
    sensitivity: 'Sensitivity',
    urgency: 'Urgency',
    lifestyle: 'Lifestyle'
  };

  // Q1's answer -> recommended bundle. This is the whole routing rule of the
  // quiz, and it MUST stay in sync with the results page.
  //
  // It is duplicated here rather than deferred because the Klaviyo record is
  // written at email submit — before the results screen would ever run — and a
  // lead without a recommended bundle is close to useless for segmentation.
  var BUNDLE_FOR_FOCUS = {
    skin: 'mature_skin',
    hair: 'mature_hair',
    both: 'mature_skin_hair'
  };

  // Bundle key -> storefront product title, so Klaviyo names the product the
  // same way the storefront does. Product IDs (never handles — handles are
  // translated per locale) live on the results page, not here.
  var BUNDLE_LABELS = {
    mature_skin: 'The Complete System for Mature Women\u2019s Skin',
    mature_hair: 'The Complete System For Mature Hair & Scalp',
    mature_skin_hair: 'Complete Natural Hair & Skin Bundle'
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Loading interstitial. Same cadence as the /pages/quiz funnel: one beat per
  // photo, then a short hold on the last one before the results appear.
  var LOADING_PHOTO_INTERVAL = 1000;
  // Beats of the loading screen: bar fill 32% → 55% → 88%, one photo collage
  // per beat. quiz-match.css keys on data-loading-state.
  var LOADING_STATES = 3;
  var LOADING_TOTAL_MS = 3500;
  // FORK CHANGE: on this quiz the top progress bar IS the loader. Fill per
  // beat — Figma draws beats 2 and 3 (57.18% / 88.97%); beat 1 is not in the
  // file and takes the ritual quiz's first beat.
  var LOADING_FILL = [32.2, 57.18, 88.97];

  // Resolve one raw answer to its label, falling back to the raw value so a
  // copy change that outpaces the map degrades to the key rather than to blank.
  function labelFor(questionId, value) {
    var map = ANSWER_LABELS[questionId];
    if (!map) return value;
    return map[value] || value;
  }

  // GTM / GA4. Mirrors the old funnel so both quizzes report through one channel.
  function pushDataLayer(event, payload) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    var data = { event: event };
    if (payload) {
      Object.keys(payload).forEach(function (key) {
        data[key] = payload[key];
      });
    }
    window.dataLayer.push(data);
  }

  // Quiz events + order attribution. window.FroyaQuiz comes from
  // snippets/quiz-analytics.liquid (rendered before this script); without it
  // every call is a no-op. The legacy pushDataLayer events stay for GTM.
  function quizTrack(eventName, payload) {
    var quiz = window.FroyaQuiz;
    if (!quiz) return;
    quiz.track(eventName, Object.assign({ quiz_id: quiz.IDS.MATCH }, payload || {}));
  }

  function QuizMatch(root) {
    this.root = root;
    this.storageKey = root.dataset.storageKey || 'froya:quiz:match:v1';

    this.steps = Array.prototype.slice.call(
      root.querySelectorAll('[data-quiz-step]')
    );
    this.progressFill = root.querySelector('[data-quiz-progress]');
    this.progressBar = root.querySelector('[data-quiz-progress-bar]');

    this.total = parseInt(root.dataset.stepsTotal, 10) || this.steps.length;
    // The bar runs one stop further than the counter — it only fills on the
    // email/results screen after the last question, not on the last question.
    this.progressTotal = parseInt(root.dataset.progressTotal, 10) || this.total + 1;
    this.index = 0;
    this.answers = {};
    // Lead identity for the email screen. Kept beside the answers rather than in
    // the form itself so a refresh mid-quiz restores what was already typed.
    this.identity = {};
    this.startedAt = Date.now();
    // Set once, in leaveForResults(), the moment the quiz hands off to the
    // results page. The results page refuses to render without it — a visitor
    // who opens that URL cold is sent back here. `index` cannot serve as the
    // flag: leaveForResults() rewinds it to the email step for the Back button.
    this.completedAt = null;
    this.advanceTimer = null;
    this.loadingTimer = null;
    this.loadingDoneTimer = null;

    this.quizName = (root.dataset.quizName || '').trim() || DEFAULT_QUIZ_NAME;
    this.emailStep = root.querySelector('[data-question-type="email"]');

    if (!this.steps.length) return;

    this.restore();
    this.bind();
    this.initEmail();
    this.render();

    // A refresh that resumes mid-quiz is not a new start.
    if (this.index === 0 && !Object.keys(this.answers).length) quizTrack('quiz_started');
  }

  /* ---------------------------------------------------------------- state -- */

  QuizMatch.prototype.restore = function () {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(this.storageKey);
    } catch (e) {
      /* private mode / storage disabled — run stateless */
    }
    if (!raw) return;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!saved || saved.v !== STORAGE_VERSION) return;

    this.answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : {};
    this.identity = saved.identity && typeof saved.identity === 'object' ? saved.identity : {};
    // Preserve the original start time across a refresh so the reported
    // duration measures the whole quiz, not the time since the last reload.
    if (typeof saved.startedAt === 'number') this.startedAt = saved.startedAt;
    if (typeof saved.completedAt === 'number') this.completedAt = saved.completedAt;

    // Resume on the furthest step reached, clamped to a step that exists.
    var resume = parseInt(saved.index, 10);
    if (!isNaN(resume) && resume > 0 && resume < this.steps.length) {
      this.index = resume;
    }

    // Re-apply the saved selections to the buttons.
    var self = this;
    this.steps.forEach(function (step) {
      var value = self.answers[step.dataset.questionId];
      if (value === undefined || value === null) return;
      var wanted = Array.isArray(value) ? value : [value];
      self.optionsIn(step).forEach(function (button) {
        var on = wanted.indexOf(button.dataset.value) !== -1;
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    });
  };

  QuizMatch.prototype.persist = function () {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({
          v: STORAGE_VERSION,
          index: this.index,
          answers: this.answers,
          identity: this.identity,
          startedAt: this.startedAt,
          completedAt: this.completedAt
        })
      );
    } catch (e) {
      /* nothing we can do; the quiz still works for this pageview */
    }
  };

  /* --------------------------------------------------------------- events -- */

  QuizMatch.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var option = event.target.closest('[data-quiz-option]');
      if (option && self.root.contains(option)) {
        self.onOption(option);
        return;
      }
      if (event.target.closest('[data-quiz-next]')) {
        self.next();
        return;
      }
      if (event.target.closest('[data-quiz-back]')) {
        self.back();
      }
    });

    // Let the device Back gesture walk back through the quiz. Step 0 is not
    // pushed, so Back from the first question leaves the page as normal.
    window.addEventListener('popstate', function (event) {
      var target = event.state && typeof event.state.quizMatchStep === 'number'
        ? event.state.quizMatchStep
        : 0;
      self.goTo(target, { history: false });
    });

    // Back/forward-cache restore (Back from the results page): the document
    // returns frozen exactly as it was left — loading step on screen, its
    // timers already spent — and a cross-document traversal fires pageshow,
    // not popstate. Re-render from the entry's state, which leaveForResults()
    // pointed at the email step. render() is called directly when the index
    // already matches, because goTo() treats a same-index call as a no-op.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      var state = window.history.state;
      var target = state && typeof state.quizMatchStep === 'number'
        ? state.quizMatchStep
        : self.index;
      if (target === self.index) {
        self.render();
        return;
      }
      self.goTo(target, { history: false });
    });
  };

  /* ------------------------------------------------------------ selection -- */

  QuizMatch.prototype.optionsIn = function (step) {
    return Array.prototype.slice
      .call(step.querySelectorAll('[data-quiz-option]'))
      // FORK CHANGE: a branching step renders EVERY variant's answers and shows
      // one. Options inside a variant that is not showing must be invisible to
      // the engine too, or they would be counted, validated and committed.
      .filter(function (option) {
        var variant = option.closest('[data-quiz-variant]');
        return !variant || variant.classList.contains('is-active');
      });
  };

  /**
   * FORK CHANGE: reveal the variant a branching step should show.
   *
   * Q2's answers depend on Q1. Every variant is server-rendered and one is
   * revealed, rather than built at runtime — so the copy stays in Liquid, stays
   * translatable, and there is no flash. Same approach the results page uses.
   *
   * Falls back to the first variant when the deciding answer is missing, which
   * happens if someone deep-links into the middle of the quiz.
   */
  QuizMatch.prototype.applyVariant = function (step) {
    var key = step.dataset.variantOf;
    if (!key) return;

    var variants = Array.prototype.slice.call(step.querySelectorAll('[data-quiz-variant]'));
    if (!variants.length) return;

    var wanted = this.answers[key];
    if (Array.isArray(wanted)) wanted = wanted[0];

    var match = null;
    variants.forEach(function (variant) {
      if (variant.dataset.quizVariant === wanted) match = variant;
    });
    if (!match) match = variants[0];

    variants.forEach(function (variant) {
      var on = variant === match;
      variant.classList.toggle('is-active', on);
      variant.hidden = !on;
    });
  };

  QuizMatch.prototype.onOption = function (button) {
    var step = button.closest('[data-quiz-step]');
    if (!step) return;

    if (step.dataset.questionType === 'multi') {
      this.onMultiSelect(step, button);
    } else {
      this.onSingleSelect(step, button);
    }

    // Every step type can carry a nav, so the forward control is re-synced here
    // rather than inside each handler.
    this.syncNext(step);
  };

  QuizMatch.prototype.onSingleSelect = function (step, button) {
    this.optionsIn(step).forEach(function (other) {
      other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
    });

    this.setAnswer(step.dataset.questionId, button.dataset.value);

    // Picking an answer IS the forward action on a single-select step, so every
    // one of them advances itself and none renders a forward control.
    // data-auto-advance="false" opts a step out if one ever needs to hold.
    if (step.dataset.autoAdvance !== 'false') {
      var self = this;
      window.clearTimeout(this.advanceTimer);
      // A short beat so the selected state is actually seen before the step
      // changes. Not a race workaround — the advance does not depend on it.
      this.advanceTimer = window.setTimeout(function () {
        self.goTo(self.index + 1);
      }, AUTO_ADVANCE_DELAY);
    }
  };

  QuizMatch.prototype.onMultiSelect = function (step, button) {
    var options = this.optionsIn(step);
    var max = parseInt(step.dataset.max, 10) || options.length;
    var isMutex = button.hasAttribute('data-quiz-mutex');
    var turningOn = button.getAttribute('aria-pressed') !== 'true';

    if (isMutex) {
      // "None of these" clears everything else.
      options.forEach(function (other) {
        other.setAttribute('aria-pressed', other === button && turningOn ? 'true' : 'false');
      });
    } else {
      if (turningOn) {
        var selected = options.filter(function (other) {
          return other.getAttribute('aria-pressed') === 'true' && !other.hasAttribute('data-quiz-mutex');
        });
        if (selected.length >= max) {
          this.showError(step, 'Choose up to ' + max);
          return;
        }
      }
      button.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
      // Any real concern clears the mutually-exclusive "none" option.
      options.forEach(function (other) {
        if (other.hasAttribute('data-quiz-mutex')) other.setAttribute('aria-pressed', 'false');
      });
    }

    this.hideError(step);

    // Commit in TAP order, not DOM order: the results page renders the Q3
    // bullets "in the order picked" (copy-logic doc §3). The DOM decides what is
    // selected; the previously saved answer decides the order of anything still
    // selected, and whatever is newly pressed goes on the end.
    var pressed = this.optionsIn(step)
      .filter(function (other) { return other.getAttribute('aria-pressed') === 'true'; })
      .map(function (other) { return other.dataset.value; });

    var prev = this.answers[step.dataset.questionId];
    if (typeof prev === 'string') prev = [prev];
    if (Object.prototype.toString.call(prev) !== '[object Array]') prev = [];

    var values = prev.filter(function (value) { return pressed.indexOf(value) !== -1; });
    pressed.forEach(function (value) {
      if (values.indexOf(value) === -1) values.push(value);
    });

    this.setAnswer(step.dataset.questionId, values);

    // FORK CHANGE: a multi-select step normally cannot know when the visitor has
    // finished choosing, so only "continue" moves it on. A step that opts in
    // with data-advance-on-max is the exception: reaching the cap IS the signal,
    // so it advances itself on the same short beat a single-select uses.
    // Only ever on the way UP — deselecting back down to the cap must not fire.
    if (step.hasAttribute('data-advance-on-max') && turningOn) {
      var cap = parseInt(step.dataset.max, 10);
      if (!isNaN(cap) && values.length >= cap) {
        var self = this;
        window.clearTimeout(this.advanceTimer);
        this.advanceTimer = window.setTimeout(function () {
          self.next();
        }, AUTO_ADVANCE_DELAY);
      }
    }
  };

  QuizMatch.prototype.setAnswer = function (questionId, value) {
    if (!questionId) return;
    this.answers[questionId] = value;
    this.persist();
    this.emit('quiz-match:answer', {
      questionId: questionId,
      value: value,
      answers: this.getAnswers()
    });
  };

  /* ------------------------------------------------------------ navigation -- */

  QuizMatch.prototype.next = function () {
    var step = this.steps[this.index];
    if (!step) return;

    if (step.dataset.questionType === 'multi') {
      var chosen = this.optionsIn(step).filter(function (other) {
        return other.getAttribute('aria-pressed') === 'true';
      });
      if (!chosen.length) {
        this.showError(step, 'Please select at least 1 option');
        return;
      }
    }
    this.goTo(this.index + 1);
  };

  QuizMatch.prototype.back = function () {
    this.goTo(this.index - 1);
  };

  QuizMatch.prototype.goTo = function (index, options) {
    options = options || {};
    if (index < 0 || index >= this.steps.length || index === this.index) return;

    // quiz_step: a forward move off a question step commits its answer.
    // History traversal (popstate / bfcache) is a revisit, not a commit.
    if (index > this.index && options.history !== false) this.trackStepCommit(this.steps[this.index]);

    window.clearTimeout(this.advanceTimer);
    this.index = index;
    this.persist();
    this.render();

    if (options.history !== false) {
      try {
        window.history.pushState({ quizMatchStep: index }, '');
      } catch (e) {
        /* history unavailable — navigation still works */
      }
    }

    // Bring the top of the quiz into view; the step above may have been taller.
    window.scrollTo({ top: 0, behavior: 'auto' });

    if (index === this.steps.length - 1) {
      this.emit('quiz-match:complete', { answers: this.getAnswers() });
    }
  };

  // One event per committed question, so drop-off shows at which one. The
  // email step carries no data-question-id and is reported by
  // quiz_email_captured instead.
  QuizMatch.prototype.trackStepCommit = function (step) {
    var questionId = step && step.dataset.questionId;
    if (!questionId) return;
    var answer = this.answers[questionId];
    var declared = parseInt(step.dataset.stepNumber, 10);
    quizTrack('quiz_step', {
      step_index: !isNaN(declared) ? declared : this.index + 1,
      question_id: questionId,
      answer: answer === undefined ? null : answer
    });
  };

  /* ---------------------------------------------------------------- render -- */

  QuizMatch.prototype.render = function () {
    var self = this;

    this.steps.forEach(function (step, i) {
      var active = i === self.index;
      step.classList.toggle('is-active', active);
      step.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        // Order matters: the variant decides which options exist, and syncNext
        // counts them to enable or disable the forward button.
        self.applyVariant(step);
        self.syncNext(step);
      }
    });

    // Warm the next step's artwork so advancing does not pop. Only ever one
    // step ahead — fetching all eight up front would waste most of them.
    this.warmStep(this.index + 1);

    // The loading interstitial runs itself. Driven from render() rather than
    // from goTo() so that a refresh which restores onto this step starts the
    // sequence too, and so leaving by ANY route — back gesture included —
    // cancels the timers rather than firing a redirect from a step the visitor
    // has already left.
    var onLoading = !!(self.steps[self.index] &&
      self.steps[self.index].dataset.questionType === 'loading');
    if (onLoading) {
      self.runLoading();
    } else {
      self.clearLoading();
    }

    var stepNumber = this.stepNumber();
    var percent = Math.min(100, (stepNumber / this.progressTotal) * 100);

    // FORK CHANGE: on the loading step the bar IS the loader, and runLoading()
    // above has just set the first beat's width. Writing the step-based width
    // here would overwrite it immediately, so the first beat never showed and
    // the bar appeared to jump backwards when the second beat landed. The
    // aria value still follows the step.
    if (this.progressFill && !onLoading) this.progressFill.style.width = percent + '%';
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(stepNumber));

    // FORK CHANGE: CSS needs to know which step is showing — the intro screen
    // empties the bar on mobile and removes it entirely on desktop. The section
    // renders this attribute server-side too, so the first paint is already
    // correct and the bar is never shown and then taken away.
    //
    // Deliberately NOT data-step-number: the steps already use that, so mirroring
    // it onto the root made `[data-step-number="2"]` match two elements — the
    // section and the step — and whichever came first in the DOM won.
    this.root.dataset.currentStep = String(stepNumber);

    this.emit('quiz-match:step', {
      index: this.index,
      stepNumber: stepNumber,
      total: this.total
    });
  };

  /**
   * Prefetch one step's decorative frame.
   *
   * The frame lives in a display:none step, so the browser will not load it on
   * its own — that is the point, but it means advancing would otherwise start
   * the download from cold. Fetching through a detached Image with the same
   * srcset/sizes lets the browser pick exactly the candidate it will later
   * need, and the response is served from cache when the step appears.
   */
  QuizMatch.prototype.warmStep = function (index) {
    var step = this.steps[index];
    if (!step) return;

    // A step may carry several decor <picture>s (art + note card); warm each.
    var pictures = step.querySelectorAll('picture');
    if (pictures.length > 1) {
      for (var p = 0; p < pictures.length; p++) this.warmPicture(pictures[p]);
      return;
    }
    var picture = pictures[0];
    if (!picture) {
      // No <picture> means a step whose artwork is plain <img> — the loading
      // interstitial's three photos. They are lazy AND inside a display:none
      // step, so nothing would fetch them until the step appeared and the first
      // beat would show an empty box. Warming them one step early (i.e. while
      // the visitor is filling in the email form) is what avoids that.
      this.warmImages(step);
      return;
    }
    this.warmPicture(picture);
  };

  /** Prefetch one <picture>, mirroring the browser's <source> choice. */
  QuizMatch.prototype.warmPicture = function (picture) {
    if (picture.dataset.warmed === 'true') return;

    var img = picture.querySelector('img');
    if (!img) return;
    picture.dataset.warmed = 'true';

    // Mirror the browser's own <source> selection for this viewport.
    var candidate = img;
    var sources = picture.querySelectorAll('source');
    for (var i = 0; i < sources.length; i++) {
      var media = sources[i].getAttribute('media');
      if (!media || (window.matchMedia && window.matchMedia(media).matches)) {
        candidate = sources[i];
        break;
      }
    }

    var pre = new Image();
    if (pre.fetchPriority !== undefined) pre.fetchPriority = 'low';
    pre.sizes = candidate.getAttribute('sizes') || '100vw';
    var srcset = candidate.getAttribute('srcset');
    if (srcset) pre.srcset = srcset;
    else pre.src = img.currentSrc || img.src;
  };

  /**
   * Prefetch every plain <img> in a step, honouring its own srcset/sizes so the
   * browser picks the exact candidate it will later render and serves it from
   * cache. Idempotent — a step is only ever warmed once.
   */
  QuizMatch.prototype.warmImages = function (step) {
    if (step.dataset.warmed === 'true') return;
    var images = step.querySelectorAll('img[srcset], img[src]');
    if (!images.length) return;
    step.dataset.warmed = 'true';

    for (var i = 0; i < images.length; i++) {
      var pre = new Image();
      if (pre.fetchPriority !== undefined) pre.fetchPriority = 'low';
      pre.sizes = images[i].getAttribute('sizes') || '100vw';
      var srcset = images[i].getAttribute('srcset');
      if (srcset) pre.srcset = srcset;
      else pre.src = images[i].getAttribute('src');
    }
  };

  // The step's own number wins, so non-question steps (email, loading) can sit
  // in the same list without throwing the "n/8" counter off.
  QuizMatch.prototype.stepNumber = function () {
    var step = this.steps[this.index];
    var declared = step && parseInt(step.dataset.stepNumber, 10);
    // FORK CHANGE: 0 is a real value here — the intro screen sits BEFORE
    // question 1, so its progress bar is empty and it has no "n/8" counter.
    // Only a missing or non-numeric value falls back to the DOM position.
    return isNaN(declared) ? this.index + 1 : declared;
  };

  QuizMatch.prototype.syncNext = function (step) {
    var button = step.querySelector('[data-quiz-next]');
    if (!button) return;

    // FORK CHANGE: a step with no options has nothing to gate on — that is the
    // intro screen, whose forward control is its CTA. Without this the CTA
    // renders permanently disabled, because "no options chosen" is always true.
    var options = this.optionsIn(step);
    if (!options.length) {
      button.disabled = false;
      return;
    }

    var chosen = options.filter(function (other) {
      return other.getAttribute('aria-pressed') === 'true';
    });
    button.disabled = chosen.length === 0;
  };

  QuizMatch.prototype.showError = function (step, message) {
    var el = step.querySelector('[data-quiz-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  QuizMatch.prototype.hideError = function (step) {
    var el = step.querySelector('[data-quiz-error]');
    if (el) el.hidden = true;
  };

  /* ------------------------------------------------------------------ api -- */

  QuizMatch.prototype.getAnswers = function () {
    return JSON.parse(JSON.stringify(this.answers));
  };

  QuizMatch.prototype.getIdentity = function () {
    return JSON.parse(JSON.stringify(this.identity || {}));
  };

  QuizMatch.prototype.isComplete = function () {
    var self = this;
    return this.steps.every(function (step) {
      if (!step.dataset.questionId) return true;
      var value = self.answers[step.dataset.questionId];
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== '';
    });
  };

  QuizMatch.prototype.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  };

  /* ---------------------------------------------------------- email step -- */
  /**
   * The screen after Q8. It is an ordinary step as far as the engine is
   * concerned — same show/hide, same progress bar, same back button — so all of
   * this hangs off the form and nothing here can affect a question step.
   *
   * The step carries no data-question-id, so isComplete() never counts it as an
   * unanswered question.
   */

  QuizMatch.prototype.initEmail = function () {
    var step = this.emailStep;
    if (!step) return;

    this.emailForm = step.querySelector('[data-quiz-email-form]');
    if (!this.emailForm) return;

    this.firstNameField = this.emailForm.querySelector('[data-quiz-first-name]');
    this.emailField = this.emailForm.querySelector('[data-quiz-email-field]');
    this.consentField = this.emailForm.querySelector('[data-quiz-consent]');
    this.submitButton = this.emailForm.querySelector('[data-quiz-email-submit]');
    this.emailError = this.emailForm.querySelector('[data-quiz-email-error]');

    var self = this;

    this.emailForm.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitEmail();
    });

    // One delegated listener for both text inputs and the checkbox, so the CTA
    // unlocks the moment the last of the three is satisfied.
    this.emailForm.addEventListener('input', function () {
      self.syncSubmit();
    });
    this.emailForm.addEventListener('change', function () {
      self.syncSubmit();
    });

    // Validate on blur, never while typing: flagging "a@" as invalid halfway
    // through the address is noise, not help.
    if (this.emailField) {
      this.emailField.addEventListener('blur', function () {
        if (self.emailField.value.trim()) self.validateEmail();
      });
    }

    var skip = step.querySelector('[data-quiz-skip]');
    if (skip) {
      skip.addEventListener('click', function () {
        self.skipEmail();
      });
    }

    this.restoreIdentity();
    this.syncSubmit();
  };

  // Put back whatever was typed before a refresh. Consent is deliberately NOT
  // restored as checked unless it genuinely was — an opt-in has to be a fresh,
  // deliberate act, and re-ticking it for the visitor would fake one.
  QuizMatch.prototype.restoreIdentity = function () {
    var identity = this.identity || {};
    if (this.firstNameField && identity.first_name) {
      this.firstNameField.value = identity.first_name;
    }
    if (this.emailField && identity.email) {
      this.emailField.value = identity.email;
    }
    if (this.consentField && identity.marketing_consent === true) {
      this.consentField.checked = true;
    }
  };

  QuizMatch.prototype.syncSubmit = function () {
    if (!this.submitButton) return;
    var name = this.firstNameField ? this.firstNameField.value.trim() : '';
    var email = this.emailField ? this.emailField.value.trim() : '';
    var consented = this.consentField ? this.consentField.checked : true;
    this.submitButton.disabled = !(name && email && consented);
  };

  QuizMatch.prototype.validateEmail = function () {
    if (!this.emailField) return true;
    var ok = EMAIL_RE.test(this.emailField.value.trim());
    this.emailField.classList.toggle('is-invalid', !ok);
    this.emailField.setAttribute('aria-invalid', ok ? 'false' : 'true');
    if (this.emailError) this.emailError.hidden = ok;
    return ok;
  };

  QuizMatch.prototype.submitEmail = function () {
    var name = this.firstNameField ? this.firstNameField.value.trim() : '';
    if (!name) {
      if (this.firstNameField) this.firstNameField.focus();
      return;
    }
    if (!this.validateEmail()) {
      this.emailField.focus();
      return;
    }

    // FORK CHANGE. The ritual quiz gates on an explicit checkbox and defaults
    // to NO consent when the field is absent — which on this quiz would have
    // silently skipped every Klaviyo call, because this design has no checkbox.
    // Here consent is given by the act of submitting: the form states
    // "By continuing you agree to receiving email with news and special
    // offers…" directly above the button, so submitting IS the opt-in.
    // A checkbox still wins if one is ever added back.
    var consent = this.consentField ? this.consentField.checked : true;

    this.identity = {
      first_name: name,
      email: this.emailField.value.trim(),
      marketing_consent: consent,
      skipped: false
    };
    this.persist();

    // Kept as a guard rather than assumed: if a checkbox is ever reinstated and
    // left unticked, no lead reaches Klaviyo.
    if (consent) {
      var properties = this.collectProperties();
      this.subscribeToKlaviyo(name, this.identity.email, properties);
      this.trackKlaviyoEvent(name, this.identity.email, properties);
    }

    pushDataLayer('quiz_email_submit', {
      quiz_name: this.quizName,
      quiz_marketing_consent: consent
    });
    quizTrack('quiz_email_captured', { subscribed: true });

    this.emit('quiz-match:email', {
      identity: JSON.parse(JSON.stringify(this.identity)),
      answers: this.getAnswers()
    });

    this.finishEmail();
  };

  QuizMatch.prototype.skipEmail = function () {
    this.identity = { skipped: true };
    this.persist();

    pushDataLayer('quiz_email_skip', { quiz_name: this.quizName });
    quizTrack('quiz_email_captured', { subscribed: false });
    this.emit('quiz-match:email', { identity: { skipped: true }, answers: this.getAnswers() });

    this.finishEmail();
  };

  /**
   * Where the quiz goes once the lead is captured: through the loading
   * interstitial, then on to the results.
   *
   * The interstitial is SKIPPED when there is nowhere for it to lead. A loading
   * screen that never resolves is worse than no loading screen, so with no
   * Results page URL and no results step the visitor stays on the email screen
   * in its submitted state instead of watching photos cycle forever.
   */
  QuizMatch.prototype.finishEmail = function () {
    var loading = this.stepIndexByType('loading');
    if (loading !== -1 && loading !== this.index && this.hasResultsDestination()) {
      this.goTo(loading);
      return;
    }
    this.goToResults();
  };

  QuizMatch.prototype.hasResultsDestination = function () {
    if ((this.root.dataset.resultsUrl || '').trim()) return true;
    return this.stepIndexByType('results') !== -1;
  };

  /**
   * The results screen is a separate build. Until it exists, set the section's
   * "Results page" setting and this redirects there; add a step with
   * data-question-type="results" later and it advances to that instead, with no
   * change here.
   *
   * With neither in place the lead is still captured and reported — the visitor
   * simply stays put, which is why the CTA is left disabled rather than bouncing
   * back to an enabled state that invites a second submit.
   */
  QuizMatch.prototype.goToResults = function () {
    var url = (this.root.dataset.resultsUrl || '').trim();
    if (url) {
      this.leaveForResults();
      window.location.href = url;
      return;
    }
    var results = this.stepIndexByType('results');
    if (results !== -1) {
      this.goTo(results);
      return;
    }
    if (this.submitButton) this.submitButton.disabled = true;
    if (this.emailStep) this.emailStep.setAttribute('data-quiz-email-state', 'submitted');
  };

  /**
   * Browser Back from the results page must land on the EMAIL step, never the
   * loading interstitial. The last history entry pushed before leaving is the
   * loading step's, so without this Back returned to a screen that either
   * re-ran the interstitial and bounced straight back to the results, or sat
   * frozen. Both places the return trip reads the step from are rewritten:
   *   - a fresh load resumes from the persisted index (restore()), so that is
   *     moved to the email step in sessionStorage;
   *   - a back/forward-cache restore keeps the frozen DOM and fires pageshow,
   *     not popstate, so the current history entry's state is replaced and the
   *     pageshow handler in bind() re-renders from it.
   * Nothing is rendered here: the loading screen stays up until the results
   * page takes over. Deliberately NOT fixed by re-running the loading timer.
   */
  QuizMatch.prototype.leaveForResults = function () {
    var email = this.stepIndexByType('email');
    if (email === -1) return;
    this.index = email;
    // The completion flag the results page gates on (quiz-results-header.liquid).
    // Only reachable through finishEmail() -> loading -> goToResults(), i.e.
    // after every question and the email step, so "finished" means finished.
    this.completedAt = Date.now();
    this.persist();
    try {
      window.history.replaceState({ quizMatchStep: email }, '', window.location.href);
    } catch (e) {
      /* history unavailable — the persisted index still covers a fresh load */
    }
  };

  QuizMatch.prototype.stepIndexByType = function (type) {
    for (var i = 0; i < this.steps.length; i++) {
      if (this.steps[i].dataset.questionType === type) return i;
    }
    return -1;
  };

  /* ------------------------------------------------------ loading screen -- */
  /**
   * Step the loading beats, then hand off to the results.
   *
   * The copy is static here — the /pages/quiz funnel rotates its text over a
   * fixed photo. The animation is the step's data-loading-state (0, 1, 2),
   * which quiz-match.css turns into the bar fill width and which of the three
   * photo collages is visible. Everything is already in the DOM and sized up front, so
   * a beat costs one attribute write and no layout.
   */
  QuizMatch.prototype.runLoading = function () {
    var step = this.steps[this.index];
    if (!step || this.loadingTimer) return;

    var self = this;
    var state = 0;

    // FORK CHANGE: mirrored onto the root so CSS can restyle the top bar as
    // the gradient loader, and the fill width follows the beat instead of the
    // step number. render() restores the normal width on the way out.
    var setBeat = function (n) {
      step.setAttribute('data-loading-state', String(n));
      self.root.dataset.loadingState = String(n);
      if (self.progressFill) self.progressFill.style.width = LOADING_FILL[n] + '%';
    };
    setBeat(0);

    this.loadingTimer = window.setInterval(function () {
      state += 1;
      // Hold on the last beat rather than looping back to the first — the
      // sequence reads as progress, and restarting it would read as a stall.
      if (state >= LOADING_STATES) {
        window.clearInterval(self.loadingTimer);
        self.loadingTimer = null;
        return;
      }
      setBeat(state);
    }, LOADING_PHOTO_INTERVAL);

    this.loadingDoneTimer = window.setTimeout(function () {
      self.loadingDoneTimer = null;
      self.goToResults();
    }, LOADING_TOTAL_MS);
  };

  QuizMatch.prototype.clearLoading = function () {
    // FORK CHANGE: drop the loader styling; render() rewrites the width.
    delete this.root.dataset.loadingState;
    if (this.loadingTimer) {
      window.clearInterval(this.loadingTimer);
      this.loadingTimer = null;
    }
    if (this.loadingDoneTimer) {
      window.clearTimeout(this.loadingDoneTimer);
      this.loadingDoneTimer = null;
    }
  };

  /* ------------------------------------------------------------- reporting -- */

  // Q1's answer decides the bundle; see BUNDLE_FOR_FOCUS.
  QuizMatch.prototype.recommendedBundle = function () {
    return BUNDLE_FOR_FOCUS[this.answers.focus] || null;
  };

  // The flat {property: value} object shared by the Klaviyo profile properties
  // and the Klaviyo event. Every name is prefixed with the quiz identifier.
  QuizMatch.prototype.collectProperties = function () {
    var props = {};
    var prefix = this.quizName + ' ';
    var self = this;

    Object.keys(PROPERTY_STEMS).forEach(function (questionId) {
      var name = prefix + PROPERTY_STEMS[questionId];
      var raw = self.answers[questionId];
      if (raw === undefined || raw === null) return;

      if (Array.isArray(raw)) {
        props[name] = raw;
        props[name + ' Label'] = raw.map(function (value) {
          return labelFor(questionId, value);
        });
      } else {
        props[name] = raw;
        props[name + ' Label'] = labelFor(questionId, raw);
      }
    });

    var bundle = this.recommendedBundle();
    if (bundle) {
      props[prefix + 'Recommended Bundle'] = bundle;
      props[prefix + 'Recommended Bundle Label'] = BUNDLE_LABELS[bundle] || bundle;
    }

    props[prefix + 'Duration Seconds'] = Math.round((Date.now() - this.startedAt) / 1000);
    props['Quiz Name'] = this.quizName;

    return props;
  };

  // Single consent gate for every outbound call. Consentmo sets __kla_off=true
  // in theme.liquid when the visitor declines marketing.
  QuizMatch.prototype.klaviyoAllowed = function () {
    return !/(?:^|;\s*)__kla_off=true/.test(document.cookie);
  };

  /**
   * Subscribe the lead via Klaviyo's client-side Subscribe API.
   *
   * Profile properties always hold the LATEST answers — a retake overwrites
   * them. The event below is what preserves history.
   *
   * Fire-and-forget with keepalive, and every failure is swallowed: a reporting
   * problem must never be able to block the visitor from reaching their results.
   */
  QuizMatch.prototype.subscribeToKlaviyo = function (firstName, email, quizProperties) {
    var companyId = this.root.dataset.klaviyoCompanyId;
    var listId = this.root.dataset.klaviyoListId;
    if (!companyId || !listId || !email) return;
    if (!this.klaviyoAllowed()) return;

    var source = this.quizName + ' Quiz';
    var properties = { 'Lead Source': source, 'Quiz Lead': true };
    Object.keys(quizProperties || {}).forEach(function (key) {
      properties[key] = quizProperties[key];
    });

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    this.postToKlaviyo('subscriptions', companyId, payload);
  };

  /**
   * Timestamped record of the completed quiz. Unlike profile properties this
   * accumulates, so retakes are preserved and flows can trigger on the metric.
   * Klaviyo auto-creates the metric on first receipt — nothing to set up in the UI.
   */
  QuizMatch.prototype.trackKlaviyoEvent = function (firstName, email, quizProperties) {
    var companyId = this.root.dataset.klaviyoCompanyId;
    if (!companyId || !email) return;
    if (!this.klaviyoAllowed()) return;

    var source = this.quizName + ' Quiz';
    var properties = { 'Lead Source': source };
    Object.keys(quizProperties || {}).forEach(function (key) {
      properties[key] = quizProperties[key];
    });

    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Completed ' + source }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, first_name: firstName }
            }
          }
        }
      }
    };

    this.postToKlaviyo('events', companyId, payload);
  };

  QuizMatch.prototype.postToKlaviyo = function (endpoint, companyId, payload) {
    try {
      window.fetch(
        'https://a.klaviyo.com/client/' + endpoint + '/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload),
          // The submit usually navigates away immediately; keepalive is what
          // stops the browser cancelling the request as the page unloads.
          keepalive: true
        }
      ).catch(function () {});
    } catch (e) {
      /* never let a tracking failure break the quiz */
    }
  };

  /* -------------------------------------------------------- reviews slider -- */
  /**
   * Q1's three-card review carousel. Independent of the step engine — it owns
   * only the cards inside [data-quiz-reviews] — so it cannot affect answers,
   * progress or navigation.
   *
   * Reader-driven: it never moves on its own. Swipe/drag it, tap a side card to
   * pull it in, or use the dots.
   *
   * Moving never touches the DOM: the selected card is always the middle child,
   * so advancing is a matter of rewriting each card's CSS `order`. The scale and
   * fade are handled entirely by the stylesheet.
   *
   * Deliberately not a native overflow-x scroller. The selected card is larger
   * than its neighbours, so whichever card is centred changes the row's layout —
   * under scroll-snap that feeds back into the scroll position and the centre
   * card can oscillate. Discrete steps have no such loop.
   */

  var SWIPE_THRESHOLD = 30; // px of travel before a drag counts as a swipe

  function ReviewsSlider(root) {
    this.root = root;
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-review]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-review-dot]'));
    this.count = this.cards.length;
    this.index = 0;
    this.pointerX = null;
    this.lastX = null;

    if (this.count < 2) return;
    this.bind();
  }

  ReviewsSlider.prototype.show = function (index) {
    var n = this.count;
    // Which slot the selected card occupies: dead centre of an odd row.
    var middle = Math.floor(n / 2);
    this.index = ((index % n) + n) % n;

    var self = this;
    this.cards.forEach(function (card, i) {
      card.style.order = String((i - self.index + middle + n) % n);
      card.classList.toggle('is-current', i === self.index);
    });
    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-current', i === self.index);
    });
  };

  ReviewsSlider.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var dot = event.target.closest('[data-quiz-review-dot]');
      if (dot) {
        self.show(parseInt(dot.dataset.quizReviewDot, 10) || 0);
        return;
      }
      // Tapping a flanking card pulls it into the centre. Guarded on the drag
      // distance so the click that ends a swipe cannot also count as a tap.
      if (self.dragged) return;
      var card = event.target.closest('[data-quiz-review]');
      if (card) self.show(self.cards.indexOf(card));
    });

    // Drag/swipe. Passive throughout — the row never needs to block scrolling,
    // so a horizontal drag is read without costing vertical scroll latency.
    //
    // Three things make this work on EVERY device, not just a mouse:
    //  1. The track has `touch-action: pan-y` (quiz-match.css). Without it a
    //     mobile browser treats any touch pan as a page scroll, takes the
    //     gesture and fires `pointercancel` — the swipe never reached us.
    //  2. Pointer capture: the finger/mouse routinely leaves the 89px-tall row
    //     before lifting, and without capture `pointerup` is delivered to
    //     whatever is under it, not to this root.
    //  3. `dragstart` is suppressed: a mouse drag that starts on a card's <img>
    //     otherwise becomes a native image drag and no `pointerup` ever fires.
    // `pointermove` tracks the last position so that even a `pointercancel`
    // (older browsers still emit one mid-gesture) can settle the swipe.
    function settle(clientX) {
      if (self.pointerX === null) return;
      var dx = clientX - self.pointerX;
      self.pointerX = null;
      self.lastX = null;
      self.dragged = Math.abs(dx) >= SWIPE_THRESHOLD;
      if (self.dragged) self.show(self.index + (dx < 0 ? 1 : -1));
    }

    this.root.addEventListener('pointerdown', function (event) {
      if (event.isPrimary === false) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      // A mouse press is the start of a drag, never of a text selection: the
      // default action of pointerdown is where the browser begins one, and
      // user-select alone does not stop a selection that is extended INTO the
      // row from text outside it. Mouse only, so touch keeps its passive path
      // (blocking touch here cannot stop scrolling anyway; touch-action does).
      // Taps still work — click is unaffected by cancelling pointerdown.
      if (event.pointerType === 'mouse') event.preventDefault();
      self.pointerX = event.clientX;
      self.lastX = event.clientX;
      self.dragged = false;
      try { self.root.setPointerCapture(event.pointerId); } catch (e) { /* not supported */ }
    }, { passive: false });

    this.root.addEventListener('pointermove', function (event) {
      if (self.pointerX === null) return;
      self.lastX = event.clientX;
    }, { passive: true });

    this.root.addEventListener('pointerup', function (event) {
      settle(event.clientX);
    }, { passive: true });

    this.root.addEventListener('pointercancel', function () {
      // Settle from the last known position instead of throwing the gesture
      // away — with touch-action set this is rare, but harmless when it fires.
      if (self.lastX !== null && self.lastX !== undefined) settle(self.lastX);
      else { self.pointerX = null; self.dragged = false; }
    }, { passive: true });

    this.root.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  };

  /* ------------------------------------------------------------ chat off -- */
  /**
   * Remove the Mimir chat launcher for the whole quiz.
   *
   * REMOVED, not hidden, and that is not a shortcut — it is the only thing that
   * works. The widget's host is #mimir-bubble-v2-root, a body-level div whose
   * launcher lives in an open shadow root. Its shadow stylesheet carries an
   * !important display rule on :host, and for !important declarations the
   * cascade order REVERSES: shadow-tree styles beat the document. Measured in
   * the browser — with `display: none !important` sitting in the element's own
   * inline style attribute, computed display was still `block`. So no
   * stylesheet rule and no inline style can hide this from the page.
   *
   * Taking the node out of the DOM sidesteps the cascade entirely. The observer
   * handles remounts, since the app recreates the root after teardown.
   */
  var CHAT_ROOTS = '#mimir-bubble-v2-root, #mimirContainer, .right-bubble-right';

  function hideChat() {
    var i;
    var known = document.querySelectorAll(CHAT_ROOTS);
    for (i = 0; i < known.length; i++) known[i].remove();

    // Fallback if the app renames its root again: find whoever is hosting a
    // shadow tree with the launcher in it, and drop that.
    var tops = document.body ? document.body.children : [];
    for (i = tops.length - 1; i >= 0; i--) {
      var el = tops[i];
      if (!el.shadowRoot) continue;
      try {
        if (el.shadowRoot.querySelector('.launcher[data-side], .launcher-button')) el.remove();
      } catch (e) {
        /* closed shadow root — unreachable from here */
      }
    }
  }

  function watchChat() {
    hideChat();
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { hideChat(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------------- boot -- */

  function initReviews(root) {
    var wrap = root.querySelector('[data-quiz-reviews]');
    if (!wrap) return null;
    return new ReviewsSlider(wrap);
  }

  function boot() {
    var root = document.querySelector('[data-quiz-match]');
    if (!root || root.dataset.quizMatchReady === 'true') return;
    root.dataset.quizMatchReady = 'true';
    window.FroyaMatchQuiz = new QuizMatch(root);
    window.FroyaMatchQuiz.reviews = initReviews(root);
    watchChat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-quiz-match]')) {
      var root = event.target.querySelector('[data-quiz-match]');
      root.dataset.quizMatchReady = 'true';
      window.FroyaMatchQuiz = new QuizMatch(root);
      window.FroyaMatchQuiz.reviews = initReviews(root);
    }
  });
})();
